/** Primary deep-video results view (creator metrics dashboard). */
export const DEEP_VIDEO_ANALYSIS_PATH = '/analysis/metrics';

/** Full category-level analysis (all schema fields). */
export const DEEP_VIDEO_FULL_ANALYSIS_PATH = '/analysis';

export type DeepVideoViewMode = 'metrics' | 'full';

/** Router state shared between metrics and full analysis views. */
export type DeepVideoAnalysisNavState = {
  viewMode?: DeepVideoViewMode;
  fromWelcome?: boolean;
  fromStatus?: boolean;
  fromCompletion?: boolean;
  requestId?: string;
  selectedFile?: { video_name?: string; file_name?: string; name?: string };
};

export type MetricsPathInput = {
  video?: string | null;
  requestId?: string | null;
};

/**
 * Canonical metrics URL: `/analysis/metrics?video=&requestId=`
 * Prefer both when known so refresh + deep links resolve the same run.
 */
export function buildMetricsPath(input: MetricsPathInput = {}): string {
  const params = new URLSearchParams();
  const video = input.video?.trim();
  const requestId = input.requestId?.trim();
  if (video) params.set('video', video);
  if (requestId) params.set('requestId', requestId);
  const query = params.toString();
  return query ? `${DEEP_VIDEO_ANALYSIS_PATH}?${query}` : DEEP_VIDEO_ANALYSIS_PATH;
}

export function buildFullAnalysisPath(input: MetricsPathInput = {}): string {
  const params = new URLSearchParams();
  const video = input.video?.trim();
  const requestId = input.requestId?.trim();
  if (video) params.set('video', video);
  if (requestId) params.set('requestId', requestId);
  const query = params.toString();
  return query ? `${DEEP_VIDEO_FULL_ANALYSIS_PATH}?${query}` : DEEP_VIDEO_FULL_ANALYSIS_PATH;
}

/** Navigate to full analysis without auto-bouncing back to the metrics dashboard. */
export function buildFullAnalysisNavState<T extends DeepVideoAnalysisNavState | null | undefined>(
  state: T,
): DeepVideoAnalysisNavState & NonNullable<T> {
  return {
    ...(state ?? {}),
    viewMode: 'full',
    fromWelcome: false,
    fromStatus: false,
    fromCompletion: false,
  } as DeepVideoAnalysisNavState & NonNullable<T>;
}

export function buildMetricsAnalysisNavState<T extends DeepVideoAnalysisNavState | null | undefined>(
  state: T,
): DeepVideoAnalysisNavState & NonNullable<T> {
  return {
    ...(state ?? {}),
    viewMode: 'metrics',
    fromWelcome: false,
    fromStatus: false,
    fromCompletion: false,
  } as DeepVideoAnalysisNavState & NonNullable<T>;
}
